import React, { useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { Badge, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";

import { VideoUploadStatus } from "@fitness-app/data-models/entities/MediaLibrary";
import {
  getStudioClipWindow,
  HOOK_TYPE_LABELS,
  HOOK_TYPE_LIFT,
  StudioClipKind,
  StudioClipStatus,
} from "@fitness-app/data-models/entities/StudioClip";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";
import { ClipEditorModal } from "./ClipEditorModal";
import { ClipQcTags } from "./ClipQcTags";
import { VariantPreviewModal } from "./VariantPreviewModal";
import { useUpdateClipStatus, type StudioClipRow } from "../hooks/useStudioData";

const STATUS_BADGES: Record<
  StudioClipStatus,
  { badge: "default" | "processing" | "success" | "error" | "warning"; label: string }
> = {
  [StudioClipStatus.Candidate]: { badge: "default", label: "Kandydat" },
  [StudioClipStatus.Rendering]: { badge: "processing", label: "Render" },
  [StudioClipStatus.RenderFailed]: { badge: "error", label: "Błąd renderu" },
  [StudioClipStatus.QcFailed]: { badge: "error", label: "Odrzucony przez QC" },
  [StudioClipStatus.Ready]: { badge: "warning", label: "Do decyzji" },
  [StudioClipStatus.Approved]: { badge: "success", label: "Zaakceptowany" },
  [StudioClipStatus.Rejected]: { badge: "default", label: "Odrzucony" },
  [StudioClipStatus.Published]: { badge: "success", label: "Opublikowany" },
};

/**
 * Bunny hands back a thumbnail URL right away but serves nothing behind it until encoding
 * finishes (pattern from MediaLibrary), so until `ready` the cell explains what is going on
 * instead of showing a broken image or a bare "…".
 */
const BUNNY_PENDING_LABELS: Record<VideoUploadStatus, string> = {
  [VideoUploadStatus.Pending]: "Wysyłanie do Bunny",
  [VideoUploadStatus.Queued]: "W kolejce Bunny",
  [VideoUploadStatus.Processing]: "Bunny przetwarza",
  [VideoUploadStatus.Encoding]: "Bunny koduje",
  [VideoUploadStatus.Ready]: "Gotowe",
  [VideoUploadStatus.Failed]: "Błąd kodowania",
};

const BUNNY_PENDING_HINT =
  "Bunny jeszcze koduje wideo. Miniatura i podgląd pojawią się same, gdy kodowanie się skończy.";

const ThumbnailPlaceholder = ({
  label,
  hint,
  busy,
  failed,
}: {
  label: string;
  hint?: string;
  busy?: boolean;
  failed?: boolean;
}): React.ReactElement => {
  const box = (
    <div
      className={`flex h-[45px] w-[80px] flex-col items-center justify-center gap-0.5 rounded px-1 text-center text-[10px] leading-tight ${
        failed ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-500"
      }`}
    >
      {busy && <LoadingOutlined spin />}
      <span>{label}</span>
    </div>
  );

  return hint ? <Tooltip title={hint}>{box}</Tooltip> : box;
};

const FRAMING_LABELS: Record<string, string> = {
  single: "kadr na mówiącą",
  split: "kadr na dwie osoby",
  pad: "pełny obraz z pasami",
};

const formatDuration = ({ startMs, endMs }: { startMs: number; endMs: number }) => {
  const totalSeconds = Math.round((endMs - startMs) / 1000);
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

interface ClipsTableProps {
  clips: StudioClipRow[];
  loading: boolean;
  /** Tryb „Gotowce": bez decyzji, z pobieraniem. */
  readonly?: boolean;
}

/**
 * Lista kandydatów z uzasadnieniem agenta i wynikiem QC (TRE-1983). Uzasadnienie jest
 * obowiązkowe w widoku — człowiek weryfikuje wybór, nie tylko go klepie. Klip odrzucony
 * przez bramkę QC widać, ale akceptacja wymaga świadomego obejścia (osobny Popconfirm).
 */
export const ClipsTable = ({ clips, loading, readonly }: ClipsTableProps): React.ReactElement => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [editedClip, setEditedClip] = useState<StudioClipRow | null>(null);
  const [previewedVariant, setPreviewedVariant] = useState<StudioClipRow | null>(null);
  const updateStatus = useUpdateClipStatus();

  return (
    <>
      <Table
        dataSource={clips}
        loading={loading}
        rowKey="id"
        size="small"
        locale={{ emptyText: "Brak klipów — rozpocznij produkcję z wybranego nagrania." }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: true }}
        expandable={{
          expandedRowRender: (clip) => (
            <div className="max-w-3xl">
              <Typography.Paragraph className="!mb-1">
                <strong>Uzasadnienie agenta:</strong> {clip.reasoning}
              </Typography.Paragraph>
              {clip.renderError && (
                <Typography.Paragraph type="danger" className="!mb-0">
                  Błąd renderu: {clip.renderError}
                </Typography.Paragraph>
              )}
            </div>
          ),
        }}
      >
        <Table.Column<StudioClipRow>
          title="Miniatura"
          width={92}
          render={(_, clip) => {
            const media = clip.clipMedia;
            const isReady = media?.data.uploadStatus === VideoUploadStatus.Ready;
            const playable = media?.data.hlsUrl || media?.data.mp4Url;

            if (isReady && media?.data.thumbnailUrl) {
              return (
                <button
                  type="button"
                  className="relative cursor-pointer border-0 bg-transparent p-0"
                  onClick={() => playable && setPreviewUrl(playable)}
                >
                  {/* Bunny oddaje URL miniatury od razu, ale serwuje ją dopiero po enkodzie —
                      wcześniejsze pokazanie to zepsuty obrazek (wzorzec z MediaLibrary). */}
                  <img src={media.data.thumbnailUrl} className="h-auto max-w-[80px]" alt={clip.hook} />
                  <PlayCircleOutlined className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl text-white" />
                </button>
              );
            }

            if (media) {
              const uploadStatus = media.data.uploadStatus;
              const failed = uploadStatus === VideoUploadStatus.Failed;
              return (
                <ThumbnailPlaceholder
                  label={BUNNY_PENDING_LABELS[uploadStatus] ?? "Bunny przetwarza"}
                  hint={failed ? undefined : BUNNY_PENDING_HINT}
                  busy={!failed}
                  failed={failed}
                />
              );
            }

            if (clip.status === StudioClipStatus.Rendering) {
              return <ThumbnailPlaceholder label="Render" busy />;
            }
            if (clip.status === StudioClipStatus.RenderFailed) {
              return <ThumbnailPlaceholder label="Błąd renderu" failed />;
            }
            return <ThumbnailPlaceholder label="Bez renderu" />;
          }}
        />
        <Table.Column<StudioClipRow>
          title="Hook"
          dataIndex="hook"
          render={(hook: string, clip) => (
            <div>
              <Typography.Text strong>{hook}</Typography.Text>
              {clip.hookType && (
                <Tooltip title={`Typ otwarcia · mnożnik skuteczności ×${HOOK_TYPE_LIFT[clip.hookType] ?? "?"}`}>
                  <Tag
                    className="ml-2"
                    color={
                      (HOOK_TYPE_LIFT[clip.hookType] ?? 1) >= 1.2
                        ? "green"
                        : (HOOK_TYPE_LIFT[clip.hookType] ?? 1) < 0.7
                          ? "orange"
                          : "default"
                    }
                  >
                    {HOOK_TYPE_LABELS[clip.hookType] ?? clip.hookType}
                  </Tag>
                </Tooltip>
              )}
              {clip.kind === StudioClipKind.Variant && clip.parts && (
                <div className="text-xs text-gray-600">
                  {clip.parts.map((part) => `${part.role.toUpperCase()} ${formatDuration(part)}`).join(" → ")}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {clip.topic} ·{" "}
                {clip.parts?.length
                  ? formatDuration({
                      startMs: 0,
                      endMs: clip.parts.reduce((sum, part) => sum + (part.endMs - part.startMs), 0),
                    })
                  : formatDuration(getStudioClipWindow(clip))}
                {clip.edit ? ` · po edycji (rew. ${clip.revision})` : ""} · źródło: {clip.source?.title ?? "?"}
                {clip.renderMeta?.framing ? (
                  <Tooltip title={clip.renderMeta.framing.reason}>
                    <span> · {FRAMING_LABELS[clip.renderMeta.framing.mode] ?? clip.renderMeta.framing.mode}</span>
                  </Tooltip>
                ) : null}
                {clip.renderMeta?.audio ? (
                  <Tooltip
                    title={`Głośność zintegrowana (cel −14 LUFS), szczyt ${clip.renderMeta.audio.truePeakDb.toFixed(1)} dBTP`}
                  >
                    <span> · {clip.renderMeta.audio.integratedLufs.toFixed(1)} LUFS</span>
                  </Tooltip>
                ) : null}
              </div>
            </div>
          )}
        />
        <Table.Column<StudioClipRow>
          title="Ocena"
          dataIndex="score"
          width={80}
          sorter={(a, b) => a.score - b.score}
          render={(score: number) => <strong>{score}</strong>}
        />
        <Table.Column<StudioClipRow> title="QC" width={220} render={(_, clip) => <ClipQcTags qc={clip.qc} />} />
        <Table.Column<StudioClipRow>
          title="Status"
          width={150}
          filters={Object.entries(STATUS_BADGES).map(([value, { label }]) => ({ value, text: label }))}
          onFilter={(value, clip) => clip.status === value}
          render={(_, clip) => {
            const config = STATUS_BADGES[clip.status] ?? { badge: "default" as const, label: clip.status };
            return <Badge status={config.badge} text={config.label} />;
          }}
        />
        <Table.Column<StudioClipRow>
          title="Akcje"
          width={readonly ? 80 : 260}
          render={(_, clip) => {
            const downloadUrl = clip.clipMedia?.data.mp4Url;

            // Playback lives on the thumbnail; the actions column only holds decisions.
            if (readonly) {
              return (
                <Space>
                  {downloadUrl && (
                    <Tooltip title="Pobierz MP4">
                      <Button size="small" icon={<DownloadOutlined />} href={downloadUrl} target="_blank" />
                    </Tooltip>
                  )}
                </Space>
              );
            }

            const isDecidable = clip.status === StudioClipStatus.Ready || clip.status === StudioClipStatus.QcFailed;
            const needsOverride = clip.status === StudioClipStatus.QcFailed;
            const isVariant = clip.kind === StudioClipKind.Variant;
            const isEditable = !isVariant && clip.status !== StudioClipStatus.Rendering;

            return (
              <Space>
                {isVariant && (
                  <Tooltip title="Podgląd bez renderu; render uruchomisz z podglądu">
                    <Button size="small" icon={<PlayCircleOutlined />} onClick={() => setPreviewedVariant(clip)}>
                      Podgląd
                    </Button>
                  </Tooltip>
                )}
                {isEditable && (
                  <Tooltip title="Przytnij klip albo popraw napisy, potem renderuj ponownie">
                    <Button size="small" icon={<EditOutlined />} onClick={() => setEditedClip(clip)} />
                  </Tooltip>
                )}
                {isDecidable && (
                  <Popconfirm
                    title={needsOverride ? "Klip nie przeszedł QC" : "Zaakceptować klip?"}
                    description={
                      needsOverride
                        ? "Bramka QC zablokowała ten klip. Akceptujesz go mimo to, na własną odpowiedzialność."
                        : "Klip zostanie zaakceptowany i będzie gotowy do publikacji."
                    }
                    okText={needsOverride ? "Akceptuję mimo QC" : "Akceptuj"}
                    cancelText="Anuluj"
                    okButtonProps={needsOverride ? { danger: true } : undefined}
                    onConfirm={() => updateStatus.mutate({ clipId: clip.id, status: StudioClipStatus.Approved })}
                  >
                    <Button size="small" type="primary" icon={<CheckOutlined />} danger={needsOverride}>
                      Akceptuj
                    </Button>
                  </Popconfirm>
                )}
                {isDecidable && (
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => updateStatus.mutate({ clipId: clip.id, status: StudioClipStatus.Rejected })}
                  >
                    Odrzuć
                  </Button>
                )}
              </Space>
            );
          }}
        />
      </Table>

      {previewUrl && <VideoPlayer videoUrl={previewUrl} visible onClose={() => setPreviewUrl(null)} />}
      {editedClip && <ClipEditorModal key={editedClip.id} clip={editedClip} onClose={() => setEditedClip(null)} />}
      {previewedVariant && (
        <VariantPreviewModal
          key={previewedVariant.id}
          clip={previewedVariant}
          onClose={() => setPreviewedVariant(null)}
        />
      )}
    </>
  );
};
